import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { PACKAGE_PRICES } from '../../config/contracts';
import { useContracts } from '../../hooks/useContract';
import { formatUsdt, formatTimestamp } from '../../utils/format';
import { getPackageColor } from '../../config/packageColors';

const PackageCard = ({ pkgId, userId, isActive, isLocked, onActivate, onRetopup }) => {
  const price = PACKAGE_PRICES[pkgId - 1];
  const { storage } = useContracts();
  const color = getPackageColor(pkgId);

  const [pkgData, setPkgData] = useState(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isActive || !userId) return;
      const myRun = ++runIdRef.current;
      try {
        const p = await storage.userPackages(userId, pkgId);
        if (cancelled || myRun !== runIdRef.current) return;
        setPkgData({
          isActive: p[0],
          activationTime: p[1],
          totalInvested: p[2],
          totalEarned: p[3],
          ceilingLimit: p[4]
        });
      } catch (_) { /* ignore */ }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, pkgId, isActive]);

  const percent = pkgData && !pkgData.ceilingLimit.isZero()
    ? Math.min(100, pkgData.totalEarned.mul(10000).div(pkgData.ceilingLimit).toNumber() / 100)
    : 0;

  const remainingBn = pkgData
    ? (pkgData.ceilingLimit.gt(pkgData.totalEarned)
        ? pkgData.ceilingLimit.sub(pkgData.totalEarned)
        : ethers.BigNumber.from(0))
    : ethers.BigNumber.from(0);

  const remainingPercent = 100 - percent;
  const ceilingHit = percent >= 100;

  // Total activation count = totalInvested / price. Includes the initial activation as 1.
  // e.g. price $5, totalInvested $15 → count = 3 (initial + 2 retopups)
  const priceBn = ethers.utils.parseUnits(String(price), 18);
  const activationCount = pkgData && !priceBn.isZero()
    ? Math.max(1, pkgData.totalInvested.div(priceBn).toNumber())
    : 0;

  const cardStyle = {
    '--pkg-from': color.from,
    '--pkg-to': color.to,
    '--pkg-glow': color.glow
  };

  const handleCardClick = () => {
    if (isLocked) return;
    if (!isActive && onActivate) onActivate(pkgId);
  };

  return (
    <div
      className={`pkg-card ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
      onClick={handleCardClick}
      style={{
        ...cardStyle,
        cursor: (!isLocked && !isActive) ? 'pointer' : 'default'
      }}
    >
      <div className="pkg-card-topbar"></div>
      <div className="pkg-card-glow"></div>

      <div className="pkg-card-head">
        <div className="pkg-badge-wrap">
          <div className="pkg-card-badge">P{pkgId}</div>
          {isActive && activationCount > 0 && (
            <div className="pkg-count-badge">{activationCount}</div>
          )}
        </div>
        {isActive && !ceilingHit && <span className="badge badge-success">Active</span>}
        {isActive && ceilingHit && <span className="badge badge-warning">Ceiling Hit</span>}
        {isLocked && <span className="badge badge-warning">🔒 Locked</span>}
        {!isActive && !isLocked && <span className="pkg-badge-cta">Tap ⚡</span>}
      </div>

      <div className="pkg-card-price">
        <span className="pkg-currency">$</span>{price}
      </div>
      <div className="pkg-card-tier">Package Tier {pkgId}</div>

      {isActive && pkgData && (
        <>
          {/* Ceiling progress */}
          <div className="pkg-card-progress">
            <div className="pkg-detail-row-inline">
              <span className="pkg-detail-inline-label">Ceiling Filled</span>
              <span className="pkg-detail-inline-value" style={{ color: color.from }}>
                {percent.toFixed(1)}%
              </span>
            </div>
            <div className="pkg-progress-track">
              <div className="pkg-progress-fill" style={{ width: `${percent}%` }}></div>
            </div>
          </div>

          {/* Detail grid: Ceiling / Earned / Balance */}
          <div className="pkg-detail-grid">
            <div className="pkg-detail-cell">
              <div className="pkg-detail-cell-label">Ceiling</div>
              <div className="pkg-detail-cell-value">${formatUsdt(pkgData.ceilingLimit, 0)}</div>
              <div className="pkg-detail-cell-sub">5x tier</div>
            </div>

            <div className="pkg-detail-cell pkg-detail-cell-earned">
              <div className="pkg-detail-cell-label">Earned</div>
              <div className="pkg-detail-cell-value">${formatUsdt(pkgData.totalEarned)}</div>
              <div className="pkg-detail-cell-sub">{percent.toFixed(1)}%</div>
            </div>

            <div className="pkg-detail-cell pkg-detail-cell-remaining">
              <div className="pkg-detail-cell-label">Balance</div>
              <div className="pkg-detail-cell-value">${formatUsdt(remainingBn)}</div>
              <div className="pkg-detail-cell-sub">{remainingPercent.toFixed(1)}% left</div>
            </div>
          </div>

          <div className="pkg-card-since">
            Since {formatTimestamp(pkgData.activationTime).split(',')[0]}
          </div>

          {onRetopup && (
            <button
              className="btn btn-full btn-sm pkg-retopup-btn mt-md"
              onClick={(e) => {
                e.stopPropagation();
                onRetopup(pkgId);
              }}
            >
              🔁 Retopup ${price}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default PackageCard;
